/**
 * The "matched by" indicator on patient search results.
 *
 * The API returns, per row, each word that was typed and the fields it matched -
 * [{ term: 'Jane', fields: ['firstName'] }, ...]. This turns that into the words
 * staff read. The API decides WHAT matched; nothing here re-derives it.
 */

export const FIELD_LABELS = {
    firstName: 'first name',
    lastName: 'last name',
    email: 'email',
    id: 'patient ID',
    phone: 'phone number',
    dateOfBirth: 'date of birth',
    address: 'address',
    child: "child's details",
};

/** A field the API sent that this build does not know is shown as sent, not hidden. */
const labelFor = field => FIELD_LABELS[field] || field;

/**
 * Each word, with the fields it matched, ready to render.
 *
 * A word the API returned with NO fields is left out rather than shown as
 * "matched by nothing": that means the database matched on something the
 * indicator cannot see, and saying nothing is more honest than implying the row
 * should not be there.
 *
 * @param {{term: string, fields: string[]}[]|undefined} searchMatches
 * @returns {{term: string, labels: string[]}[]}
 */
export function describeMatches(searchMatches) {
    if (!Array.isArray(searchMatches)) {
        return [];
    }

    return searchMatches
        .filter(match => match && Array.isArray(match.fields) && match.fields.length)
        .map(match => ({ term: match.term, labels: match.fields.map(labelFor) }));
}

/**
 * Just the fields, each once - for the search dropdown, which is too narrow to
 * repeat every word.
 *
 * @param {{term: string, fields: string[]}[]|undefined} searchMatches
 * @returns {string[]}
 */
export function matchedFieldLabels(searchMatches) {
    const seen = [];

    describeMatches(searchMatches).forEach((match) => {
        match.labels.forEach((label) => {
            if (seen.indexOf(label) === -1) {
                seen.push(label);
            }
        });
    });

    return seen;
}
